import _ from "lodash";
import React from "react";

import {
  FieldError,
  IFormField
} from "src/library/components/Forms";
import { AddAttachments } from "src/library/components/Forms/AddAttachments";
import { validateField } from "src/library/utils/validation";
import { ISupportTicketExtraDataFormQuestion } from "src/modules/support/api/support.api";

interface UseAdditionalDataFormFieldsResultType {
  generateAdditionalFields: (formQuestions: ISupportTicketExtraDataFormQuestion[]) => IFormField[];
}

/**
 * @description Hook to provide functionality for additional data form fields.
 *
 * @returns {UseAdditionalDataFormFieldsResultType}
 */
export const useAdditionalDataFormFields = (): UseAdditionalDataFormFieldsResultType => {
  // Generate additional form fields based on the form questions provided.
  const generateAdditionalFields = React.useCallback((formQuestions: ISupportTicketExtraDataFormQuestion[]): IFormField[] => {
    const supportTicketFormFields: IFormField[] = [];

    // Generate the appropriate form field for each form question
    for (const formQuestion of formQuestions) {
      // Set up the form field
      let formFieldConfig: IFormField = {
        type: formQuestion.formFieldType || "input",
        name: formQuestion.key,
        inputType: formQuestion.type,
        label: _.startCase(formQuestion.label)
      };

      // Add validation to the form field
      if (formQuestion.validation) {
        formFieldConfig.validation = validateField(formQuestion.validation, formQuestion.type);
      }

      // Add (select field) options to the form field
      if (formQuestion.options) {
        formFieldConfig = {
          ...formFieldConfig,
          options: formQuestion.options.map(o => ({
            value: o,
            text: _.startCase(o)
          })),
          unselectedOption: {
            text: "Please select an option",
            value: ""
          }
        };
      }

      if (formQuestion.formFieldType === "attachments") {
        formFieldConfig = {
          ...formFieldConfig,
          Component: ({ setFieldValue, error }) => (
            <>
              <AddAttachments
                setAttachments={files => setFieldValue(formQuestion.key, files)}
                title={formQuestion.title}
                description={formQuestion.description}
              />

              {error && (
                <FieldError>
                  {error}
                </FieldError>
              )}
            </>
          )
        };
      }

      supportTicketFormFields.push(formFieldConfig);
    }

    // Return the generated fields
    return supportTicketFormFields;
  }, []);

  return { generateAdditionalFields };
};
